// The deal's dialogs (spec 09 §4.14; plan §7's New deal / Lost / Move row):
// New deal, Move, Mark lost, Mark won and Link a quote. Each is the register's
// `Modal`, which traps focus and closes on Escape in the capture phase, so a
// dialog opened inside the side peek closes alone and the peek stays
// (design-system §3.12).
//
// THE DIALOGS DECIDE NOTHING ABOUT THE BOARD. Move reports the reader's choice
// and the caller carries it out: the board lays it over its columns and rolls
// it back on a refusal, the deal page and the peek send it and read the deal
// again. New deal, Link and the two close dialogs send their own request,
// because none of them has a board to keep in step.
//
// THE VALUE IS A VALUE READER'S. The create and the edit refuse
// `expected_value_minor` from a caller without `sales.read_value` (403, D-312
// ruling 12), so the value box is offered only when the role mirror does not
// KNOW the reader is refused (`valueSortVerdict`); a tenant's composed role
// the mirror cannot judge is offered it and meets the server's worded answer.
// An empty box sends no value, which the deal stores as "nobody estimated
// it", never as zero (D-312 ruling 7).
//
// MARK WON NAMES THE BUSINESS THAT WON, WHEN THERE IS ONE (D-312 ruling 9).
// The dialog lists the customer's converted quotes and confirmed orders that
// the server's win door accepts (`winRowVerdict`), and "no document", which
// wins on the expected value. Mark won and Link a quote judge every row from
// the list row alone (#293): the quote and order lists say which deal a row
// belongs to and whether another deal holds it, so neither picker reads a
// quote's detail per row. A named order is what the forecast's Won cell
// counts at its total; before the picker a direct order could never back a
// win from the office. The accepted-quote notice opens the dialog with its
// quote already named and no picker, so the writer confirms without choosing.
//
// EVERY PICKER WALKS ITS PAGES. Mark won, Link a quote and the composer's
// Link to a deal read a page at a time with Show more (`PickerTail`), so a
// customer's 51st quote or order is reachable.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Field } from "../../../../../components/field";
import { Modal } from "../../../../../components/modal";
import { RefLabel } from "../../../../../components/ref-label";
import { Select } from "../../../../../components/select";
import { StatusChip } from "../../../../../components/status-chip";
import { CardSkeleton, OfflineState } from "../../../../../components/states";
import { facilityTodayIso } from "../../../../../lib/business-dates";
import { foldPagedRows } from "../../../../../lib/paged-rows";
import { RequestError } from "../../../../../lib/request";
import { useTenantTimezone } from "../../../../../lib/role-codes";
import { useDebouncedValue } from "../../../../../lib/use-debounced-value";
import type { DropTarget } from "../../lib/board-state";
import { orderStatusVariant } from "../../lib/chips";
import { formatMinor } from "../../lib/money";
import {
  addMonths,
  isLostReasonCode,
  LOST_REASON_CODES,
  lostReasonLabel,
  parseValueInput,
  pipelineRemedyOf,
  quoteDealChangeable,
  refusalOf,
  stageName,
  type LostReasonCode,
} from "../../lib/pipeline";
import { quoteStatusVariant } from "../../lib/quotes";
import { valueSortVerdict } from "../../lib/value-order";
import { MutationFailureNote } from "../../mutation-failure-note";
import { canReassignOpportunityOwner, useSalesPermissions } from "../../hooks/permissions";
import { useOnboardingPermissions } from "../../../../onboarding/hooks/permissions";
import { useCustomer360, useCustomers, useOwnerIndex } from "../../hooks/sales-queries";
import {
  useOrderPages,
  useQuotePages,
  type Quote,
  type SalesOrder,
} from "../../hooks/quote-queries";
import {
  useCreateOpportunity,
  useLinkQuoteOpportunity,
  useOpportunity,
  type Opportunity,
  type OpportunityDetail,
  type OpportunityWinRequest,
  type PipelineStage,
} from "../../hooks/opportunity-queries";

/** A title's bound (spec 09 §2.25). */
export const DEAL_TITLE_MAX = 200;
/** A lost note's bound (spec 09 §2.25). */
export const LOST_NOTE_MAX = 2000;

/** A refusal line under a dialog: the sentence and the request id. A
 * refusal that names its remedy (D-343: `PIPELINE_NOT_CONFIGURED` carries
 * `remedy: "apply_pack_update"`) also offers it to a reader who can take it,
 * the same link the board's no-stages state shows. */
export function RefusalLine({
  error,
  fallback,
  stageNameOf,
  testId,
}: {
  readonly error: unknown;
  readonly fallback: string;
  readonly stageNameOf?: ((id: string) => string | undefined) | undefined;
  readonly testId: string;
}) {
  const { t } = useTranslation();
  const onboardingPerms = useOnboardingPermissions();
  const refusal = refusalOf(t, error, fallback, stageNameOf);
  const remedy =
    pipelineRemedyOf(error) === "apply_pack_update" &&
    onboardingPerms.has("onboarding.install_pack");
  return (
    <>
      <MutationFailureNote data-testid={testId} text={refusal.text} error={error} />
      {remedy ? (
        <Link to="/settings/industry-pack" className="ds-lnk" data-testid={`${testId}-remedy`}>
          {t("sales.pipeline.noStagesAction")}
        </Link>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// New deal
// ---------------------------------------------------------------------------

type NewDealField = "title" | "customer" | "value" | "close";

export interface NewDealModalProps {
  /** A customer the address or the 360 fixed; the picker is not offered. */
  readonly lockedCustomerId?: string | undefined;
  /** The module 17 conversation the deal comes from (the inbox's door). */
  readonly fromConversation?: string | undefined;
  /** The tenant's month from the board read, for the default close month. */
  readonly tenantMonth?: string | undefined;
  readonly stages: readonly PipelineStage[];
  readonly onClose: () => void;
  readonly onCreated?: ((deal: Opportunity) => void) | undefined;
}

export function NewDealModal({
  lockedCustomerId,
  fromConversation,
  tenantMonth,
  stages,
  onClose,
  onCreated,
}: NewDealModalProps) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const create = useCreateOpportunity();
  const valueOffered = valueSortVerdict(perms) !== "refused";
  const canNameOwner = canReassignOpportunityOwner(perms);
  const owners = useOwnerIndex(canNameOwner);

  const [title, setTitle] = useState("");
  const [customerText, setCustomerText] = useState("");
  const customerQ = useDebouncedValue(customerText.trim(), 250);
  const [pickedCustomer, setPickedCustomer] = useState("");
  const customerId = lockedCustomerId ?? pickedCustomer;
  const matches = useCustomers(
    { q: customerQ },
    undefined,
    lockedCustomerId === undefined && customerQ.length > 1,
  );
  const customer = useCustomer360(customerId);
  const currency = customer.data?.customer.default_currency;
  const [valueText, setValueText] = useState("");
  // Next month on the PLANT's calendar (D-089): the board hands in the month
  // its read served, and any other opener derives it from the tenant's zone,
  // which falls back to the browser calendar only while the feed is in
  // flight. A default the reader has not touched follows the zone once it
  // lands.
  const timeZone = useTenantTimezone();
  const defaultClose =
    addMonths(tenantMonth ?? facilityTodayIso(Date.now(), timeZone).slice(0, 7), 1) ?? "";
  const [closeMonth, setCloseMonth] = useState(defaultClose);
  const closeTouched = useRef(false);
  useEffect(() => {
    if (!closeTouched.current) setCloseMonth(defaultClose);
  }, [defaultClose]);
  const [stageId, setStageId] = useState(stages[0]?.id ?? "");
  const [owner, setOwner] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // A value typed before the customer's currency is known cannot be read yet.
  // It is never sent as "no value": the create waits, and says why.
  const valueTyped = valueText.trim().length > 0;
  const value =
    currency === undefined
      ? valueTyped
        ? undefined
        : null
      : parseValueInput(valueText, currency, i18n.language);
  const clientErrors: Partial<Record<NewDealField, string>> = {};
  const trimmed = title.trim();
  if (trimmed.length === 0) clientErrors.title = t("sales.pipeline.create.titleRequired");
  else if (trimmed.length > DEAL_TITLE_MAX)
    clientErrors.title = t("sales.pipeline.create.titleTooLong");
  if (customerId.length === 0) clientErrors.customer = t("sales.pipeline.create.customerRequired");
  if (valueOffered && valueTyped && currency === undefined)
    clientErrors.value = t("sales.pipeline.create.valueWaiting");
  else if (valueOffered && value === undefined)
    clientErrors.value = t("sales.pipeline.create.valueInvalid");
  if (closeMonth.length === 0) clientErrors.close = t("sales.pipeline.create.closeMonthRequired");
  const shown = (field: NewDealField) => (submitted ? clientErrors[field] : undefined);

  const submit = () => {
    setSubmitted(true);
    if (Object.keys(clientErrors).length > 0) return;
    create.mutate(
      {
        customer_id: customerId,
        title: trimmed,
        expected_close_month: closeMonth,
        ...(stageId.length > 0 ? { stage_id: stageId } : {}),
        ...(valueOffered && typeof value === "number" ? { expected_value_minor: value } : {}),
        ...(canNameOwner && owner.length > 0 ? { owner_user_id: owner } : {}),
        ...(fromConversation !== undefined ? { source_conversation_id: fromConversation } : {}),
      },
      {
        onSuccess: (deal) => {
          onCreated?.(deal);
          onClose();
        },
      },
    );
  };

  const errorLine = (field: NewDealField) => {
    const text = shown(field);
    return text === undefined ? null : (
      <span className="field-error" role="alert" data-testid={`sales-deal-new-error-${field}`}>
        {text}
      </span>
    );
  };

  return (
    <Modal
      heading={t("sales.pipeline.create.title")}
      label={t("sales.pipeline.create.title")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-deal-new-modal"
      pending={create.isPending}
    >
      <div className="sales-form">
        <Field label={t("sales.pipeline.create.titleLabel")} wide>
          <input
            value={title}
            maxLength={DEAL_TITLE_MAX + 50}
            data-testid="sales-deal-new-title"
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
          {errorLine("title")}
        </Field>
        {lockedCustomerId !== undefined ? (
          <Field label={t("sales.pipeline.create.customerLabel")} wide>
            <span data-testid="sales-deal-new-customer-locked" data-customer={lockedCustomerId}>
              <RefLabel
                value={customer.data?.customer.display_name}
                id={lockedCustomerId}
                pending={customer.isPending}
              />
            </span>
          </Field>
        ) : (
          <>
            <Field label={t("sales.pipeline.create.customerLabel")} wide>
              <input
                value={customerText}
                placeholder={t("sales.pipeline.create.customerHint")}
                data-testid="sales-deal-new-customer-q"
                onChange={(event) => {
                  setCustomerText(event.target.value);
                  setPickedCustomer("");
                }}
              />
              {errorLine("customer")}
            </Field>
            {customerQ.length > 1 ? (
              <Field label={t("sales.pipeline.create.customerLabel")} wide>
                <Select
                  value={pickedCustomer}
                  data-testid="sales-deal-new-customer"
                  onChange={(event) => {
                    setPickedCustomer(event.target.value);
                  }}
                >
                  <option value="">{t("sales.pipeline.anyCustomer")}</option>
                  {(matches.data?.items ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
          </>
        )}
        {fromConversation !== undefined ? (
          <p
            className="sales-hint"
            data-testid="sales-deal-new-from-conversation"
            data-conversation={fromConversation}
          >
            {t("sales.pipeline.create.fromConversation")}
          </p>
        ) : null}
        {valueOffered ? (
          <>
            <Field label={t("sales.pipeline.create.valueLabel")}>
              <input
                value={valueText}
                inputMode="decimal"
                data-testid="sales-deal-new-value"
                aria-describedby="sales-deal-new-value-hint"
                onChange={(event) => {
                  setValueText(event.target.value);
                }}
              />
              {errorLine("value")}
            </Field>
            <Field label={t("sales.pipeline.create.currencyLabel")}>
              <input
                value={currency ?? ""}
                readOnly
                data-testid="sales-deal-new-currency"
                aria-readonly="true"
              />
            </Field>
            <p className="sales-hint" id="sales-deal-new-value-hint">
              {t("sales.pipeline.create.valueHint")}
            </p>
          </>
        ) : null}
        <Field label={t("sales.pipeline.create.closeMonthLabel")}>
          <input
            type="month"
            value={closeMonth}
            data-testid="sales-deal-new-close"
            onChange={(event) => {
              closeTouched.current = true;
              setCloseMonth(event.target.value);
            }}
          />
          {errorLine("close")}
        </Field>
        <Field label={t("sales.pipeline.create.stageLabel")}>
          <Select
            value={stageId}
            data-testid="sales-deal-new-stage"
            onChange={(event) => {
              setStageId(event.target.value);
            }}
          >
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stageName(stage, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
        {canNameOwner ? (
          <Field label={t("sales.pipeline.create.ownerLabel")}>
            <Select
              value={owner}
              data-testid="sales-deal-new-owner"
              onChange={(event) => {
                setOwner(event.target.value);
              }}
            >
              <option value="">{t("sales.pipeline.create.ownerMe")}</option>
              {[...owners.byId.entries()].map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>
      {create.isError ? (
        <RefusalLine
          error={create.error}
          fallback={t("sales.pipeline.create.failed")}
          testId="sales-deal-new-failed"
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button
          variant="primary"
          data-testid="sales-deal-new-confirm"
          busy={create.isPending}
          busyLabel={t("sales.pipeline.create.creating")}
          onClick={submit}
        >
          {t("sales.pipeline.create.confirm")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Move
// ---------------------------------------------------------------------------

function targetValue(target: DropTarget): string {
  return target.kind === "stage" ? `stage:${target.stageId}` : target.kind;
}

function targetOf(value: string): DropTarget | undefined {
  if (value === "won") return { kind: "won" };
  if (value === "lost") return { kind: "lost" };
  if (value.startsWith("stage:")) return { kind: "stage", stageId: value.slice(6) };
  return undefined;
}

/** The Move dialog: every stage, then Won and Lost. It reports the choice and
 * leaves the request to the caller (the header says why). */
export function MoveDealModal({
  title,
  currentStageId,
  stages,
  onClose,
  onChoose,
}: {
  readonly title: string;
  readonly currentStageId: string;
  readonly stages: readonly PipelineStage[];
  readonly onClose: () => void;
  readonly onChoose: (target: DropTarget) => void;
}) {
  const { t, i18n } = useTranslation();
  const [choice, setChoice] = useState(`stage:${currentStageId}`);
  const chosen = targetOf(choice);
  const unchanged = chosen?.kind === "stage" && chosen.stageId === currentStageId;
  const option = (target: DropTarget, label: string) => {
    const value = targetValue(target);
    return (
      <label
        key={value}
        className="sales-radio"
        data-testid="sales-deal-move-option"
        data-target={value}
      >
        <input
          type="radio"
          name="sales-deal-move"
          value={value}
          checked={choice === value}
          onChange={() => {
            setChoice(value);
          }}
        />
        <span>{label}</span>
      </label>
    );
  };
  return (
    <Modal
      heading={t("sales.pipeline.move.title", { title })}
      label={t("sales.pipeline.move.title", { title })}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-deal-move-modal"
    >
      <div className="sales-form sales-radio-list">
        <fieldset className="sales-fieldset">
          <legend className="sales-hint">{t("sales.pipeline.move.stagesLabel")}</legend>
          {stages.map((stage) => {
            const name = stageName(stage, i18n.language);
            return option(
              { kind: "stage", stageId: stage.id },
              stage.id === currentStageId
                ? t("sales.pipeline.move.current", { stage: name })
                : name,
            );
          })}
        </fieldset>
        <fieldset className="sales-fieldset">
          <legend className="sales-hint">{t("sales.pipeline.move.closeLabel")}</legend>
          {option({ kind: "won" }, t("sales.pipeline.board.wonZone"))}
          {option({ kind: "lost" }, t("sales.pipeline.board.lostZone"))}
        </fieldset>
      </div>
      <div className="sales-modal-actions">
        <Button
          variant="primary"
          data-testid="sales-deal-move-confirm"
          disabled={chosen === undefined || unchanged}
          onClick={() => {
            if (chosen === undefined || unchanged) return;
            onChoose(chosen);
          }}
        >
          {t("sales.pipeline.move.confirm")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mark lost
// ---------------------------------------------------------------------------

/** The reason the newest rejected linked quote carries, which the Lost dialog
 * pre-selects (spec 09 §4.14). `linked_quotes` is newest first. */
export function newestRejectionReason(
  deal: Pick<OpportunityDetail, "linked_quotes"> | undefined,
): LostReasonCode | undefined {
  for (const quote of deal?.linked_quotes ?? []) {
    if (quote.status === "rejected" && isLostReasonCode(quote.rejection_reason_code)) {
      return quote.rejection_reason_code;
    }
  }
  return undefined;
}

export interface LoseChoice {
  readonly reason: LostReasonCode;
  readonly note: string | undefined;
}

export function LoseDealModal({
  dealId,
  title,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  readonly dealId: string;
  readonly title: string;
  readonly onClose: () => void;
  readonly onConfirm: (choice: LoseChoice) => void;
  readonly pending: boolean;
  readonly error: unknown;
}) {
  const { t } = useTranslation();
  // The deal's own read names its newest rejected quote; the board's card
  // does not carry the linked quotes, so the dialog asks for them itself.
  const deal = useOpportunity(dealId);
  const suggested = newestRejectionReason(deal.data);
  const [picked, setPicked] = useState<LostReasonCode | undefined>(undefined);
  const reason = picked ?? suggested;
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const noteTooLong = note.length > LOST_NOTE_MAX;
  const reasonMissing = reason === undefined;

  const confirm = () => {
    setSubmitted(true);
    if (reason === undefined || noteTooLong) return;
    const trimmed = note.trim();
    onConfirm({ reason, note: trimmed.length > 0 ? note : undefined });
  };

  return (
    <Modal
      heading={t("sales.pipeline.lose.title", { title })}
      label={t("sales.pipeline.lose.title", { title })}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-deal-lose-modal"
      pending={pending}
    >
      <div className="sales-form sales-radio-list">
        <fieldset className="sales-fieldset">
          <legend className="sales-hint">{t("sales.pipeline.lose.reasonLabel")}</legend>
          {LOST_REASON_CODES.map((code) => (
            <label key={code} className="sales-radio">
              <input
                type="radio"
                name="sales-deal-lose-reason"
                value={code}
                checked={reason === code}
                data-testid="sales-deal-lose-reason"
                onChange={() => {
                  setPicked(code);
                }}
              />
              <span>{lostReasonLabel(t, code)}</span>
            </label>
          ))}
          {submitted && reasonMissing ? (
            <span className="field-error" role="alert" data-testid="sales-deal-lose-error-reason">
              {t("sales.pipeline.lose.reasonRequired")}
            </span>
          ) : null}
        </fieldset>
        <Field label={t("sales.pipeline.lose.noteLabel")} wide>
          <textarea
            value={note}
            rows={3}
            placeholder={t("sales.pipeline.lose.noteHint")}
            data-testid="sales-deal-lose-note"
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          {noteTooLong ? (
            <span className="field-error" role="alert" data-testid="sales-deal-lose-error-note">
              {t("sales.pipeline.lose.noteTooLong")}
            </span>
          ) : null}
        </Field>
        <p className="sales-hint" data-testid="sales-deal-lose-stays-open">
          {t("sales.pipeline.lose.staysOpen")}
        </p>
      </div>
      {error !== undefined && error !== null ? (
        <RefusalLine
          error={error}
          fallback={t("sales.pipeline.lose.failed")}
          testId="sales-deal-lose-failed"
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button
          variant="danger"
          data-testid="sales-deal-lose-confirm"
          busy={pending}
          busyLabel={t("sales.pipeline.lose.saving")}
          onClick={confirm}
        >
          {t("sales.pipeline.lose.confirm")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// A picker's pages (the house page shape)
// ---------------------------------------------------------------------------

/** One page read of a picker, as the tail reads it. */
export interface PickerRead {
  readonly data: { readonly next_cursor: string | null } | undefined;
  readonly error: unknown;
  readonly fetchStatus: string;
  readonly refetch?: (() => unknown) | undefined;
}

/**
 * THE PAGE SHAPE OF A PICKER. The books show one page at a time with Previous
 * and Next (D-560); a picker inside a dialog is the one place a cursor is still
 * walked with Show more (`sales.common.loadMore`), because the reader is
 * choosing one row from a short scan rather than reading a book: the rows
 * already read stay, Show more reads the next page on the reader's press, and
 * the page in flight states itself. A first page that failed, paused or is
 * still loading stands in the list's place, and a later one stands under the
 * rows, so a failed later page is read again by the same press and never
 * takes the rows already shown with it. A picker that read one page of 50 and
 * stopped left the 51st quote or deal with no way to be chosen.
 *
 * A REFUSAL IS NOT A FAILURE (design-system §3.9). When the caller names what
 * a 403 means for its dialog, the refusal reads as that sentence, with no
 * request id and no Show more, because asking again answers the same.
 */
export function PickerTail({
  read,
  hasRows,
  testPrefix,
  errorText,
  refusedText,
  onMore,
}: {
  readonly read: PickerRead;
  /** Whether rows from earlier pages are on the glass. */
  readonly hasRows: boolean;
  /** `-error`, `-refused`, `-offline`, `-loading` and `-more` are appended. */
  readonly testPrefix: string;
  readonly errorText: string;
  /** What a 403 on the read means here; absent, a 403 reads as `errorText`. */
  readonly refusedText?: string | undefined;
  readonly onMore: (cursor: string) => void;
}) {
  const { t } = useTranslation();
  const more = (busy: boolean, onClick: () => void) => (
    <div className="sales-toolbar">
      <Button
        size="sm"
        data-testid={`${testPrefix}-more`}
        busy={busy}
        busyLabel={t("sales.common.loading")}
        onClick={onClick}
      >
        {t("sales.common.loadMore")}
      </Button>
    </div>
  );
  if (read.data === undefined) {
    if (
      refusedText !== undefined &&
      read.error instanceof RequestError &&
      read.error.httpStatus === 403
    ) {
      return (
        <p className="sales-hint" role="status" data-testid={`${testPrefix}-refused`}>
          {refusedText}
        </p>
      );
    }
    if (read.error !== null && read.error !== undefined) {
      const refetch = read.refetch;
      return (
        <>
          <MutationFailureNote
            data-testid={`${testPrefix}-error`}
            text={errorText}
            error={read.error}
          />
          {hasRows && refetch !== undefined
            ? more(false, () => {
                void refetch();
              })
            : null}
        </>
      );
    }
    if (read.fetchStatus === "paused") {
      return (
        <div data-testid={`${testPrefix}-offline`}>
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        </div>
      );
    }
    return hasRows ? (
      more(true, () => undefined)
    ) : (
      <div data-testid={`${testPrefix}-loading`}>
        <CardSkeleton lines={2} label={t("sales.common.loading")} />
      </div>
    );
  }
  const next = read.data.next_cursor;
  return next === null
    ? null
    : more(false, () => {
        onMore(next);
      });
}

/**
 * A PICKER'S CURSOR WALK OVER LIVE PAGES (#293 review). The walk holds only
 * the cursors it has read. Each page is its own observed query
 * (`useOrderPages`, `useQuotePages`, `useOpportunityPages`), so a write that
 * invalidates the list reads every walked page again, and a row on an earlier
 * page is judged on its fresh facts. The first cut kept a snapshot of the
 * earlier pages, so after Show more a row a 409 had just refused stayed
 * offered and Confirm sent it again.
 */
export function usePickerCursors() {
  const [cursors, setCursors] = useState<readonly (string | undefined)[]>([undefined]);
  return {
    cursors,
    advance: (next: string) => {
      setCursors((stack) => (stack.includes(next) ? stack : [...stack, next]));
    },
  };
}

/** The read a walk that has no page yet states: the first page loading. */
const NO_PAGE_YET: PickerRead = { data: undefined, error: null, fetchStatus: "fetching" };

/**
 * The walk as the picker renders it (pure, unit-tested): every walked page's
 * rows folded by id in page order (`foldPagedRows`, so a row a refetch
 * updated keeps its place), and the LAST page's read, whose Show
 * more, failure or pause `PickerTail` states. An earlier page that is being
 * read again keeps its rows on the glass meanwhile.
 */
export function pickerView<T extends { readonly id: string }>(
  pages: readonly (PickerRead & {
    readonly data:
      { readonly items: readonly T[]; readonly next_cursor: string | null } | undefined;
  })[],
): { readonly rows: T[]; readonly read: PickerRead } {
  let rows: T[] = [];
  for (const page of pages) rows = foldPagedRows(rows, page.data?.items ?? []);
  return { rows, read: pages.at(-1) ?? NO_PAGE_YET };
}

// ---------------------------------------------------------------------------
// Mark won
// ---------------------------------------------------------------------------

/** A win asked for with a converted quote already named: the accepted-quote
 * notice's path, which opens Mark won with no picker (D-312 ruling 8). */
export interface WinWithQuote {
  readonly quoteId: string;
  readonly quoteNo: string;
}

/** How many of the customer's orders one page of the Mark won picker reads. */
export const WIN_PICKER_PAGE_SIZE = 50;

/** What a win names (§5's win row: a converted quote or a confirmed order,
 * never both). */
export type WinChoice =
  | {
      readonly kind: "quote";
      readonly quoteId: string;
      readonly quoteNo: string;
      readonly orderNo: string;
    }
  | { readonly kind: "order"; readonly orderId: string; readonly orderNo: string };

/** The body a choice sends (pure, unit-tested). No choice names nothing, and
 * the deal then counts its expected value, as the board's Won zone does. */
export function winBodyOf(choice: WinChoice | undefined): OpportunityWinRequest {
  if (choice === undefined) return {};
  return choice.kind === "quote"
    ? { quote_id: choice.quoteId }
    : { sales_order_id: choice.orderId };
}

/** A choice's radio value, one per business. */
export function winChoiceKey(choice: WinChoice | undefined): string {
  if (choice === undefined) return "none";
  return choice.kind === "quote" ? `quote:${choice.quoteId}` : `order:${choice.orderId}`;
}

/**
 * WHAT A LIST ROW SAYS OF THE DEAL A QUOTE BELONGS TO (pure, unit-tested;
 * #293). The quote and order lists carry the facts (spec 09 §5's quote and
 * order list rows): the deal's id when the reader's deal door answers it, and,
 * for a holder of `sales.opportunity.write`, whether the quote belongs to any
 * deal at all. So no picker reads a quote's detail per row.
 *  * `this`: the quote already belongs to the deal being worked on.
 *  * `elsewhere`: it belongs to another deal, named or one the reader cannot
 *    open. The link and win doors answer 409 `QUOTE_LINKED_ELSEWHERE`.
 *  * `free`: it belongs to no deal.
 *  * `unknown`: the row does not say, because the server is one deploy behind
 *    or the reader is not told. The row is offered and says it could not be
 *    checked, and the door's worded refusal answers the one fact.
 */
export type DealFact = "this" | "elsewhere" | "free" | "unknown";

export function dealFactOf(
  facts: {
    readonly opportunity_id?: string | null | undefined;
    readonly opportunity_linked?: boolean | undefined;
  },
  dealId: string,
): DealFact {
  const id = facts.opportunity_id;
  if (typeof id === "string") return id === dealId ? "this" : "elsewhere";
  if (facts.opportunity_linked === true) return "elsewhere";
  if (facts.opportunity_linked === false) return "free";
  return "unknown";
}

export type WinRowVerdict =
  | { readonly kind: "offer"; readonly choice: WinChoice; readonly unchecked: boolean }
  | { readonly kind: "withheld" };

const WIN_WITHHELD: WinRowVerdict = { kind: "withheld" };

/** The order list row's facts the Mark won picker judges (§5's order list
 * row). */
export type WinPickerOrder = Pick<
  SalesOrder,
  | "id"
  | "order_no"
  | "status"
  | "quote_id"
  | "quote_no"
  | "quote_opportunity_id"
  | "quote_opportunity_linked"
  | "backs_won_opportunity"
>;

/**
 * MAY THIS ORDER, OR THE CONVERTED QUOTE IT CAME FROM, WIN THIS DEAL? (pure,
 * unit-tested). The server's win door (`OpportunitiesService.win`, spec 09
 * §5's win row, D-312 ruling 9) is the rule, and the picker offers nothing
 * the row says that door refuses. Every fact comes from the order list row
 * itself (#293), so a page is judged the moment it answers.
 *  * A cancelled order wins nothing on either path (409 `ORDER_TERMINAL`).
 *  * An order a won deal already names is that deal's (409
 *    `ORDER_WON_ELSEWHERE`), whether or not the reader can open that deal.
 *  * An order whose quote belongs to another deal is that deal's (409
 *    `QUOTE_LINKED_ELSEWHERE`).
 *  * An order that came from a quote the reader can open (`quote_no` is
 *    served) is named BY ITS QUOTE, because the quote path stamps
 *    `won_quote_id` as well as `won_order_id` and links the quote. An order
 *    carries a quote only when the acceptance of that quote minted it, so the
 *    quote is converted into this order.
 *  * Any other order is named itself, and a direct order still waiting for
 *    approval is not confirmed yet (409 `ORDER_NOT_CONFIRMED`).
 * The orders come from the reader's own order list filtered by the deal's
 * customer, so `ORDER_NOT_FOUND` and `OPPORTUNITY_CUSTOMER_MISMATCH` cannot
 * arise. A row that does not say whether it is spoken for (a server one
 * deploy behind) is offered and marked unchecked, because hiding it would
 * leave a free order no way to win, and the door's worded 409 answers it.
 */
export function winRowVerdict(order: WinPickerOrder, dealId: string): WinRowVerdict {
  if (order.status === "cancelled" || order.backs_won_opportunity === true) return WIN_WITHHELD;
  const wonUnknown = order.backs_won_opportunity === undefined;
  const asOrder = (unchecked: boolean): WinRowVerdict =>
    order.status === "pending_approval"
      ? WIN_WITHHELD
      : {
          kind: "offer",
          choice: { kind: "order", orderId: order.id, orderNo: order.order_no },
          unchecked,
        };
  if (order.quote_id === null) return asOrder(wonUnknown);
  const fact = dealFactOf(
    {
      opportunity_id: order.quote_opportunity_id,
      opportunity_linked: order.quote_opportunity_linked,
    },
    dealId,
  );
  if (fact === "elsewhere") return WIN_WITHHELD;
  const unchecked = wonUnknown || fact === "unknown";
  const quoteNo = order.quote_no;
  if (typeof quoteNo === "string") {
    return {
      kind: "offer",
      choice: { kind: "quote", quoteId: order.quote_id, quoteNo, orderNo: order.order_no },
      unchecked,
    };
  }
  return asOrder(unchecked);
}

/**
 * THE MARK WON PICKER (spec 09 §4.14's Deal bullet, D-312 ruling 9). The
 * customer's orders, newest confirmed first, a page at a time, each judged by
 * `winRowVerdict` from its own list row. It reads the orders rather than the
 * quotes because every business that can win has an order: a converted quote
 * names its own, and a direct order is one. The first choice is always "no
 * document", which wins on the expected value. One list read per page and
 * nothing per row (#293).
 */
function WinPicker({
  dealId,
  customerId,
  chosenKey,
  onPick,
}: {
  readonly dealId: string;
  readonly customerId: string;
  readonly chosenKey: string;
  readonly onPick: (choice: WinChoice | undefined) => void;
}) {
  const { t, i18n } = useTranslation();
  const walk = usePickerCursors();
  const pages = useOrderPages(
    { customer_id: customerId, limit: WIN_PICKER_PAGE_SIZE },
    walk.cursors,
  );
  const { rows, read: page } = pickerView<SalesOrder>(pages);
  const judged = rows.flatMap((order) => {
    const verdict = winRowVerdict(order, dealId);
    return verdict.kind === "offer" ? [{ order, verdict }] : [];
  });
  const settledEmpty = page.data !== undefined && judged.length === 0;
  // A choice the picker no longer offers (a concurrent win took its order,
  // and the list read again after it now says so, on whichever walked page
  // the order sits) falls back to no document at once, even while Show more
  // is still reading the next page, so Confirm never sends a row the reader
  // can no longer see. A choice can only be made from a row on the glass, and
  // a walked page keeps its rows while it is read again, so a row that leaves
  // the offer has been withdrawn.
  const offeredKeys = new Set(judged.map(({ verdict }) => winChoiceKey(verdict.choice)));
  const choiceWithheld = chosenKey !== "none" && !offeredKeys.has(chosenKey);
  useEffect(() => {
    if (choiceWithheld) onPick(undefined);
  }, [choiceWithheld, onPick]);

  return (
    <fieldset className="sales-fieldset" data-testid="sales-deal-win-picker">
      <legend className="sales-hint">{t("sales.pipeline.win.pickLabel")}</legend>
      <label className="sales-radio" data-testid="sales-deal-win-none">
        <input
          type="radio"
          name="sales-deal-win-choice"
          value="none"
          checked={chosenKey === "none"}
          onChange={() => {
            onPick(undefined);
          }}
        />
        <span>{t("sales.pipeline.win.none")}</span>
      </label>
      {judged.map(({ order, verdict }) => {
        const { choice } = verdict;
        const key = winChoiceKey(choice);
        return (
          <label
            key={order.id}
            className="sales-radio"
            data-testid="sales-deal-win-option"
            data-choice={key}
          >
            <input
              type="radio"
              name="sales-deal-win-choice"
              value={key}
              checked={chosenKey === key}
              onChange={() => {
                onPick(choice);
              }}
            />
            <span>
              {choice.kind === "quote"
                ? t("sales.pipeline.win.optionQuote", {
                    quote: choice.quoteNo,
                    order: choice.orderNo,
                  })
                : t("sales.pipeline.win.optionOrder", { order: choice.orderNo })}
            </span>
            <StatusChip variant={orderStatusVariant(order.status)}>
              {t(`sales.orders.status.${order.status}`)}
            </StatusChip>
            {order.total_minor !== undefined ? (
              <span className="num">
                {formatMinor(order.total_minor, order.currency, i18n.language)}
              </span>
            ) : null}
            {verdict.unchecked ? (
              <span className="sales-hint" data-testid="sales-deal-win-unchecked">
                {t("sales.pipeline.deal.linkUnchecked")}
              </span>
            ) : null}
          </label>
        );
      })}
      {settledEmpty ? (
        <p
          className="sales-hint"
          data-testid={
            page.data.next_cursor === null ? "sales-deal-win-empty" : "sales-deal-win-none-yet"
          }
        >
          {page.data.next_cursor === null
            ? t("sales.pipeline.win.empty")
            : t("sales.pipeline.win.noneYet")}
        </p>
      ) : null}
      <PickerTail
        read={page}
        hasRows={rows.length > 0}
        testPrefix="sales-deal-win"
        errorText={t("sales.pipeline.win.listError")}
        refusedText={t("sales.pipeline.win.ordersRefused")}
        onMore={(next) => {
          walk.advance(next);
        }}
      />
    </fieldset>
  );
}

export function WinDealModal({
  title,
  dealId,
  customerId,
  named,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  readonly title: string;
  readonly dealId: string;
  readonly customerId: string;
  /** The converted quote the accepted-quote notice names. The dialog then
   * names it and offers no picker, so the writer confirms without choosing. */
  readonly named?: WinWithQuote | undefined;
  readonly onClose: () => void;
  readonly onConfirm: (body: OpportunityWinRequest) => void;
  readonly pending: boolean;
  readonly error: unknown;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<WinChoice | undefined>(undefined);
  return (
    <Modal
      heading={t("sales.pipeline.win.title", { title })}
      label={t("sales.pipeline.win.title", { title })}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-deal-win-modal"
      pending={pending}
    >
      <div className="sales-form sales-radio-list">
        <p className="sales-hint">{t("sales.pipeline.win.body")}</p>
        {named !== undefined ? (
          <p data-testid="sales-deal-win-quote">
            {t("sales.pipeline.deal.wonWithQuote", { quote: named.quoteNo })}
          </p>
        ) : (
          <>
            <WinPicker
              dealId={dealId}
              customerId={customerId}
              chosenKey={winChoiceKey(choice)}
              onPick={setChoice}
            />
            <p className="sales-hint" data-testid="sales-deal-win-hint">
              {t("sales.pipeline.win.pickHint")}
            </p>
          </>
        )}
      </div>
      {error !== undefined && error !== null ? (
        <RefusalLine
          error={error}
          fallback={t("sales.pipeline.win.failed")}
          testId="sales-deal-win-failed"
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button
          variant="primary"
          data-testid="sales-deal-win-confirm"
          busy={pending}
          busyLabel={t("sales.pipeline.win.saving")}
          onClick={() => {
            onConfirm(named !== undefined ? { quote_id: named.quoteId } : winBodyOf(choice));
          }}
        >
          {t("sales.pipeline.win.confirm")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Link a quote
// ---------------------------------------------------------------------------

/** How many of the customer's quotes one page of the Link a quote picker
 * reads. */
export const LINK_QUOTE_PAGE_SIZE = 50;

export type LinkRowVerdict = "offer" | "unchecked" | "withheld";

/**
 * MAY THIS QUOTE BE LINKED TO THIS DEAL? (pure, unit-tested). The link door
 * (`OpportunitiesService.linkQuote`, §5's link row) refuses a finished quote
 * (409 `QUOTE_TERMINAL`) and one that belongs to another deal (409
 * `QUOTE_LINKED_ELSEWHERE`), and a quote already on this deal needs no link.
 * The quote list row says which (`dealFactOf`, #293), including a deal the
 * reader cannot open. A row that does not say is offered and marked
 * unchecked, and the door's worded refusal answers it. `linkedIds` is the
 * deal page's own linked quotes, for a row a server one deploy behind serves
 * without its deal.
 */
export function linkRowVerdict(
  quote: Pick<Quote, "id" | "status" | "opportunity_id" | "opportunity_linked">,
  dealId: string,
  linkedIds: ReadonlySet<string>,
): LinkRowVerdict {
  if (!quoteDealChangeable(quote.status) || linkedIds.has(quote.id)) return "withheld";
  const fact = dealFactOf(quote, dealId);
  if (fact === "this" || fact === "elsewhere") return "withheld";
  return fact === "free" ? "offer" : "unchecked";
}

/** One candidate quote the Link a quote picker offers. */
function LinkCandidate({
  quoteId,
  quoteNo,
  status,
  unchecked,
  checked,
  onPick,
}: {
  readonly quoteId: string;
  readonly quoteNo: string;
  readonly status: string;
  readonly unchecked: boolean;
  readonly checked: boolean;
  readonly onPick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="sales-radio" data-testid="sales-deal-link-option" data-quote={quoteId}>
      <input
        type="radio"
        name="sales-deal-link"
        value={quoteId}
        checked={checked}
        onChange={onPick}
      />
      <span>{quoteNo}</span>
      <StatusChip variant={quoteStatusVariant(status)}>
        {t(`sales.quoteStatus.${status}`)}
      </StatusChip>
      {unchecked ? (
        <span className="sales-hint" data-testid="sales-deal-link-unchecked">
          {t("sales.pipeline.deal.linkUnchecked")}
        </span>
      ) : null}
    </label>
  );
}

export function LinkQuoteModal({
  deal,
  onClose,
}: {
  readonly deal: Pick<OpportunityDetail, "id" | "title" | "customer_id" | "linked_quotes">;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const link = useLinkQuoteOpportunity();
  const walk = usePickerCursors();
  const pages = useQuotePages(
    { customer_id: deal.customer_id, limit: LINK_QUOTE_PAGE_SIZE },
    walk.cursors,
  );
  const { rows, read: quotes } = pickerView<Quote>(pages);
  const linked = useMemo(() => new Set(deal.linked_quotes.map((q) => q.id)), [deal.linked_quotes]);
  // Each row is judged from the list alone (#293), so the dialog can count
  // what it offers: a page whose open quotes all belong to other deals says
  // so, as a page with none does.
  const offered = rows.flatMap((quote) => {
    const verdict = linkRowVerdict(quote, deal.id, linked);
    return verdict === "withheld" ? [] : [{ quote, unchecked: verdict === "unchecked" }];
  });
  const [picked, setPicked] = useState("");
  // A pick the dialog no longer offers (the press met 409
  // QUOTE_LINKED_ELSEWHERE and the list read again says another deal holds
  // it, on whichever walked page it sits) is dropped at once, the rule Mark
  // won follows, and Confirm never sends it.
  const pickedOffered = offered.some(({ quote }) => quote.id === picked);
  const pickWithheld = picked !== "" && !pickedOffered;
  useEffect(() => {
    if (pickWithheld) setPicked("");
  }, [pickWithheld]);
  const next = quotes.data?.next_cursor ?? null;

  return (
    <Modal
      heading={t("sales.pipeline.deal.linkTitle")}
      label={t("sales.pipeline.deal.linkTitle")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-deal-link-modal"
      pending={link.isPending}
    >
      <div className="sales-form sales-radio-list">
        <p className="sales-hint">{t("sales.pipeline.deal.linkHint")}</p>
        {offered.map(({ quote, unchecked }) => (
          <LinkCandidate
            key={quote.id}
            quoteId={quote.id}
            quoteNo={quote.quote_no}
            status={quote.status}
            unchecked={unchecked}
            checked={picked === quote.id}
            onPick={() => {
              setPicked(quote.id);
            }}
          />
        ))}
        {quotes.data !== undefined && offered.length === 0 ? (
          <p
            className="sales-hint"
            data-testid={next === null ? "sales-deal-link-empty" : "sales-deal-link-none-yet"}
          >
            {next === null
              ? t("sales.pipeline.deal.linkEmpty")
              : t("sales.pipeline.deal.linkNoneYet")}
          </p>
        ) : null}
        <PickerTail
          read={quotes}
          hasRows={rows.length > 0}
          testPrefix="sales-deal-link"
          errorText={t("sales.pipeline.deal.linkListError")}
          onMore={(cursor) => {
            walk.advance(cursor);
          }}
        />
      </div>
      {link.isError ? (
        <RefusalLine
          error={link.error}
          fallback={t("sales.pipeline.deal.linkFailed")}
          testId="sales-deal-link-failed"
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button
          variant="primary"
          data-testid="sales-deal-link-confirm"
          disabled={!pickedOffered}
          busy={link.isPending}
          busyLabel={t("sales.pipeline.deal.linking")}
          onClick={() => {
            if (!pickedOffered) return;
            link.mutate(
              { quoteId: picked, opportunityId: deal.id },
              {
                onSuccess: () => {
                  onClose();
                },
              },
            );
          }}
        >
          {t("sales.pipeline.deal.linkConfirm")}
        </Button>
      </div>
    </Modal>
  );
}

/** Is this a 400 whose field errors name one of the deal's fields? */
export function dealFieldError(error: unknown, field: string): boolean {
  return (
    error instanceof RequestError &&
    error.httpStatus === 400 &&
    error.fieldErrors.some((entry) => entry.path === field)
  );
}
